import {
  TournamentsTournamentsClient,
  TournamentEntryList,
  CancelReason,
  StatusDto,
  EnterDuelDto,
  TournamentEntry,
  TournamentParticipantList,
  Tournament,
  GamersSummary,
  TournamentType,
  TournamentStatus,
  StakeFiatCurrency,
  UserGameplayOutcome,
} from "@nestcoinco/hyper-api-gateway-api-client";

import apiConfig, { platformType } from "../config/api-config";
import { axiosInstance } from "../config/axios-instance";
import { PaginatedEndpointParams } from "../utils/constants/generic-types";

const tournamentsClient = new TournamentsTournamentsClient(
  apiConfig,
  undefined,
  axiosInstance
);

const handleGetTournamentPendingEntries = async (
  page?: number,
  limit?: number
): Promise<TournamentEntryList | undefined> => {
  try {
    return (
      await tournamentsClient.getMyEntries(
        page,
        limit,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
        false
      )
    ).data;
  } catch {
    return;
  }
};

const handleCancelTournamentEntry = async (
  entryId: string,
  reason?: CancelReason | undefined
): Promise<StatusDto | undefined> => {
  return (await tournamentsClient.cancelTournamentEntry(entryId, reason)).data;
};

const handleForfeitTournamentEntry = async (entryId: string) => {
  return (await tournamentsClient.forfeitTournamentEntry(entryId)).data;
};

interface GetMultiplayerTournamentsParams extends PaginatedEndpointParams {
  gameId?: string;
  currency?: StakeFiatCurrency;
  statuses?: TournamentStatus[];
}

const handleGetMultiplayerTournaments = async (
  params?: GetMultiplayerTournamentsParams
) => {
  const { page, limit, gameId, currency, statuses } = params ?? {};
  return (
    await tournamentsClient.getMultiplayerTournaments(
      page,
      limit,
      gameId,
      undefined,
      statuses,
      currency
    )
  ).data;
};

const handleEnterDuel = async (
  enterDuelDto: EnterDuelDto
): Promise<TournamentEntry> => {
  return (await tournamentsClient.enterDuel(enterDuelDto)).data;
};

const handleEnterPlatformDuel = async (
  enterDuelDto: EnterDuelDto
): Promise<TournamentEntry> => {
  return (
    await tournamentsClient.enterPlatformDuel(
      platformType,
      enterDuelDto
    )
  ).data;
};

type GetParticipantsParamsType = {
  tournamentId: string;
  page?: number;
  limit?: number;
  userCompleted?: boolean;
};

const handleGetTournamentParticipants = async ({
  tournamentId,
  page,
  limit,
  userCompleted,
}: GetParticipantsParamsType): Promise<TournamentParticipantList> => {
  return (
    await tournamentsClient.getTournamentParticipants(
      tournamentId,
      page,
      limit,
      userCompleted
    )
  ).data;
};

const handleGetMyDuelOpponent = async (
  tournamentId: string
): Promise<StatusDto> => {
  return (await tournamentsClient.getMyDuelOpponent(tournamentId, true)).data;
};

const handleGetTournamentDetails = async (
  tournamentId: string
): Promise<Tournament> => {
  return (await tournamentsClient.getTournamentDetail(tournamentId)).data;
};

const handleGetRecentGamersSummary = async (
  gameId: string
): Promise<GamersSummary> => {
  return (await tournamentsClient.getDuelRecentsGamersSummary(gameId)).data;
};

const handleGetTournamentEntryDetails = async (
  entryId: string
): Promise<TournamentEntry> => {
  return (await tournamentsClient.getEntryDetail(entryId)).data;
};

interface GetTournamentEntryListParams {
  page?: number;
  limit?: number;
  tournamentId?: string;
  type?: TournamentType;
  gameId?: string;
  preStake?: boolean;
  submitted?: boolean;
  includeExpired?: boolean;
}

const handleGetMyTournamentEntries = async (
  params?: GetTournamentEntryListParams
): Promise<TournamentEntryList | undefined> => {
  const {
    page,
    limit,
    tournamentId,
    type,
    gameId,
    preStake,
    submitted,
    includeExpired,
  } = params || {};
  return (
    await tournamentsClient.getMyEntries(
      page,
      limit,
      tournamentId,
      type,
      gameId,
      preStake,
      submitted,
      includeExpired
    )
  ).data;
};

interface GetActiveTournamentsParams extends PaginatedEndpointParams {
  gameId?: string;
}

const handleGetActiveTournaments = async (
  params: GetActiveTournamentsParams
) => {
  const { gameId, page, limit } = params;
  return (
    await tournamentsClient.listActiveMultiTournaments(gameId, page, limit)
  ).data;
};

const handleGetMyGameplayResults = async (
  limit?: number
): Promise<UserGameplayOutcome[]> => {
  return (await tournamentsClient.getMyGameplayResults(limit)).data.records;
};

export {
  handleGetTournamentPendingEntries,
  handleCancelTournamentEntry,
  handleGetMultiplayerTournaments,
  GetMultiplayerTournamentsParams,
  handleEnterDuel,
  handleGetTournamentParticipants,
  GetParticipantsParamsType,
  handleGetMyDuelOpponent,
  handleGetTournamentDetails,
  handleGetRecentGamersSummary,
  handleGetTournamentEntryDetails,
  handleGetMyTournamentEntries,
  GetTournamentEntryListParams,
  handleForfeitTournamentEntry,
  handleGetActiveTournaments,
  GetActiveTournamentsParams,
  handleGetMyGameplayResults,
  handleEnterPlatformDuel,
};
